import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Typography, Box, Button, IconButton, Tooltip,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions
} from '@mui/material';
import { Upload, PowerSettingsNew, Refresh } from '@mui/icons-material';
import { useReferenceData } from '../hooks/useReferenceData';
import FeedUploadDialog from './reference-data/FeedUploadDialog';

/**
 * ReferenceDataPage — Shows feed status table with upload and management actions.
 * Wired to the feed-based API (IRIS-374).
 */
const ReferenceDataPage = ({ customerId }) => {
  const {
    feeds,
    loading,
    error,
    success,
    fetchFeeds,
    uploadFile,
    previewUploadFile,
    deactivate,
  } = useReferenceData(customerId);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFeed, setSelectedFeed] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);

  const handleUploadClick = (feed) => {
    setSelectedFeed(feed);
    setUploadDialogOpen(true);
  };

  const handleUploadClose = () => {
    setUploadDialogOpen(false);
    setSelectedFeed(null);
  };

  const handleUploadConfirm = async (file) => {
    if (!selectedFeed) return;
    await uploadFile(selectedFeed.feedId, file);
    setUploadDialogOpen(false);
    setSelectedFeed(null);
  };

  const handleDeactivate = (feed) => {
    setDeactivateConfirm(feed);
  };

  const confirmDeactivate = async () => {
    if (!deactivateConfirm) return;
    try {
      setDeactivating(deactivateConfirm.feedId);
      await deactivate(deactivateConfirm.feedId);
    } finally {
      setDeactivating(null);
      setDeactivateConfirm(null);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'fresh': { color: 'success', label: 'Fresh' },
      'stale': { color: 'warning', label: 'Stale' },
      'config-stale': { color: 'warning', label: 'Config Stale' },
      'failed': { color: 'error', label: 'Failed' },
      'never-ingested': { color: 'default', label: 'Never Loaded' },
    };
    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatRowCount = (count) => {
    if (count == null) return '—';
    return count.toLocaleString();
  };

  if (loading && feeds.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 3 }}>
        <CircularProgress size={20} />
        <Typography>Loading reference data feeds...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Reference Data Feeds</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={fetchFeeds}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
      )}

      {!loading && feeds.length === 0 ? (
        <Alert severity="info">
          No reference data feeds configured for this customer. Feeds are provisioned by the platform team.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Feed</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Rows</strong></TableCell>
                <TableCell><strong>Last Loaded</strong></TableCell>
                <TableCell><strong>Fuzzy</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feeds.map((feed) => (
                <TableRow key={feed.feedId} hover>
                  <TableCell>{feed.displayName}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {feed.feedType}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(feed.status)}</TableCell>
                  <TableCell align="right">{formatRowCount(feed.activeVersionRowCount)}</TableCell>
                  <TableCell>{formatDate(feed.lastIngestionTime)}</TableCell>
                  <TableCell>
                    {feed.enableFuzzyMatching ? (
                      <Chip size="small" variant="outlined" color="info" label="Enabled" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Upload data file">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleUploadClick(feed)}
                        aria-label={`Upload file for ${feed.displayName}`}
                      >
                        <Upload fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate feed">
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleDeactivate(feed)}
                        disabled={deactivating === feed.feedId}
                        aria-label={`Deactivate ${feed.displayName}`}
                      >
                        <PowerSettingsNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {uploadDialogOpen && selectedFeed && (
        <FeedUploadDialog
          open={uploadDialogOpen}
          feed={selectedFeed}
          onClose={handleUploadClose}
          onConfirm={handleUploadConfirm}
          onPreview={previewUploadFile}
        />
      )}

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={!!deactivateConfirm}
        onClose={() => setDeactivateConfirm(null)}
      >
        <DialogTitle>Deactivate Feed</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate "{deactivateConfirm?.displayName}"?
            Lookup rules referencing this feed will stop resolving.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateConfirm(null)}>Cancel</Button>
          <Button onClick={confirmDeactivate} color="warning" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ReferenceDataPage.propTypes = {
  customerId: PropTypes.string.isRequired,
};

export default ReferenceDataPage;
